/* Segal name necklace — INPUT RULES (2026-09-14). Loaded after name-necklace-ui.js.
 *
 * Why: the Worker refuses names over 9 letters (too_long), more than two words, digits, links and
 * emoji, but the "Generate necklace" block only learned about it after the whole 30-second
 * animation and then said "try a different spelling" (Clementine, measured 2026-09-14). This
 * mirrors every Worker rule in the browser so the customer reads the real reason as they type,
 * Generate is disabled, and Enter does nothing - the same treatment name-necklace-ui.js already
 * gives unsupported alphabets.
 *
 * It can also PAUSE new Arabic names (ARABIC_PAUSED) - used 2026-09-14 21:40-23:45Z while the
 * plate's chain anchors were fixed (project doc Segal-Arabic-Chain-RootCause-PlateAnchors-2026-09-14):
 * the finished Arabic products stay on sale and the name already on the page is never flagged.
 *
 * Purely additive: it never touches fetch, the form or the variant. Delete the one script line in
 * snippets/name-necklace-switcher.liquid to remove it.
 */
(() => {
  'use strict';
  const cfg = window.SegalNameNecklace || {};
  const MAX_LETTERS = 9;
  const ARABIC_RE = /[؀-ۿݐ-ݿ]/;
  const ARABIC_PAUSED = false;  // 2026-09-14 23:45Z - plate v11 (outer-letter anchors) is live on this theme; paused 21:40-23:45Z

  const norm = (v) => String(v || '').trim().replace(/\s+/g, ' ');
  const pageName = () => {
    const raw = document.querySelector('[data-name-necklace-input], input[name="properties[Custom Name]"]');
    return norm(raw && raw.value);
  };
  const problemWith = (v) => {
    const s = norm(v);
    if (!s) return null;
    if (s === pageName()) return null;                       // the finished product on this page
    if (window.SegalPlate && !window.SegalPlate.supported(s)) return null;   // ui.js already handles alphabets
    if (cfg.family !== 'diamond' && ARABIC_PAUSED && ARABIC_RE.test(s))
      return 'Arabic names are getting a new chain setting today. Message us and we’ll make yours by hand, '
           + 'or choose one of the 190+ Arabic names ready now. ';
    if (Array.from(s).length > MAX_LETTERS)
      return 'Names up to ' + MAX_LETTERS + ' letters are made instantly. For a longer name, message us and we’ll cut it by hand. ';
    if (s.split(' ').length > 2) return 'One or two words, please. For a longer inscription, message us. ';
    if (/\d/.test(s) || /https?:|www\.|@/i.test(s)) return 'Letters only, please. For numbers or symbols, message us. ';
    if (/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(s)) return 'Letters only, please. For symbols, message us. ';
    return null;
  };

  const parts = () => {
    const box = document.querySelector('.snui');
    if (!box) return null;
    return { box, $in: box.querySelector('.snui__in'), $go: box.querySelector('.snui__go'), $err: box.querySelector('.snui__err') };
  };
  // Same markup ui.js uses for an unsupported alphabet, so its "Message us" click handler and its
  // clearUnsupported() (which only clears messages carrying .snui__msg) both apply to ours.
  const show = (p, msg) => {
    p.$err.hidden = false;
    p.$err.textContent = msg;
    const a = document.createElement('a');
    a.href = '#'; a.className = 'snui__msg'; a.textContent = 'Message us';
    p.$err.appendChild(a);
    p.$go.disabled = true;
  };
  // Same message, but the button stays usable: the name in the field is now valid.
  const note = (p, msg) => { show(p, msg); p.$go.disabled = false; };
  // 2026-09-14, Yaniv: "add block after 9 chars" - stop the field at 9 letters (code points, spaces
  // count, exactly as the Worker counts) instead of only warning. The page's own name is left alone.
  const clampLength = (p) => {
    const v = p.$in.value; if (norm(v) === pageName()) return false;
    const cps = Array.from(v);
    if (cps.length <= MAX_LETTERS) return false;
    p.$in.value = cps.slice(0, MAX_LETTERS).join('');
    note(p, 'Names are up to ' + MAX_LETTERS + ' letters - that is the longest we cast in one piece. For a longer name, message us and we’ll cut it by hand. ');
    return true;
  };
  const check = (fromInput) => {
    const p = parts(); if (!p || !p.$in) return null;
    if (p.box.classList.contains('snui--busy')) return null;
    if (fromInput && clampLength(p)) { const m = problemWith(p.$in.value); if (m) show(p, m); return m; }
    const msg = problemWith(p.$in.value);
    if (msg) show(p, msg);
    return msg;
  };

  let wired = false;
  const wire = () => {
    const p = parts(); if (!p || !p.$in || wired) return;
    wired = true;
    // registered after ui.js's own input listener, so it runs after clearUnsupported()
    p.$in.addEventListener('input', () => check(true));
    p.$in.setAttribute('maxlength', String(MAX_LETTERS + 1));   // +1 so the clamp (and its message) still fires on the 10th letter
    // ui.js re-renders (and re-enables the button) on load, on a timer and on metal changes
    setInterval(() => { const q = parts(); if (q && q.$in && document.activeElement !== q.$in) check(); }, 1200);
  };

  // Capture-phase guards on the document run BEFORE ui.js's own click/Enter handlers on the
  // button and the field, so a blocked name can never start a generation.
  document.addEventListener('click', (e) => {
    const go = e.target && e.target.closest && e.target.closest('.snui__go');
    if (!go) return;
    const msg = check();
    if (msg) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.keyCode !== 13) return;
    if (!e.target || !e.target.matches || !e.target.matches('.snui__in')) return;
    const msg = check();
    if (msg) { e.preventDefault(); e.stopImmediatePropagation(); }
  }, true);

  // Diamond pages: the browse copy still promised "any language"; diamond is English letters only.
  const fixDiamondCopy = () => {
    if (cfg.family !== 'diamond') return;
    const s = document.querySelector('.snui__s'); if (!s) return;
    const t = s.textContent;
    const u = t.replace(', in any language, and we will', ' in English letters, and we will')
               .replace('Type any name in any language.', 'Type any name in English letters.')
               .replace('in about 30 seconds', 'in about a minute');
    if (u !== t) s.textContent = u;
  };

  // The theme's body font is Adobe's proxima-nova declared with NO fallback list, so a Cyrillic,
  // Hebrew or Arabic name in the H1 or the input drops to the browser default serif (Times) -
  // measured on russian-gold-name-necklace-31e29af6 (Полина) 2026-09-14. Give every element that
  // resolves to bare proxima-nova a fallback stack that carries those alphabets. Inline, and only
  // where the computed font is exactly 'proxima-nova', so nothing that uses another font changes.
  const FALLBACK = 'proxima-nova, "Helvetica Neue", Arial, "Segoe UI", Roboto, sans-serif';
  const fixFontFallback = () => {
    const els = [document.body, ...document.querySelectorAll('h1, h2, h3, h4, p, input, .snui, .snui *')];
    els.forEach((el) => {
      if (!el || el.dataset.snnFont) return;
      if (getComputedStyle(el).fontFamily !== 'proxima-nova') return;
      el.style.fontFamily = FALLBACK; el.dataset.snnFont = '1';
    });
  };

  // Legacy Arabic titles ("Gold Arabic Name Necklace – Amal أمل | 14K Solid Gold") carry no
  // left-to-right mark after the Arabic word, so the bidi algorithm pulls "| 14" into the Arabic
  // run and the H1 reads "AMAL 14 | أملK SOLID GOLD". Generated titles already include U+200E.
  // Until the 50 legacy titles are fixed in the data, insert the mark where the page prints them.
  const BIDI_RE = /([؀-ۿݐ-ݿ][؀-ۿݐ-ݿ\s]*)(\s*\|)/g;
  const fixBidi = () => {
    const roots = document.querySelectorAll('h1, .product_name, .product-title, .product-recently-viewed, .breadcrumb, .breadcrumbs');
    roots.forEach((root) => {
      const walk = document.createTreeWalker(root, NodeFilter.SHOW_TEXT); let n;
      while ((n = walk.nextNode())) {
        const v = n.nodeValue || ''; if (!/[؀-ۿ]/.test(v) || v.indexOf('‎') >= 0) continue;
        const u = v.replace(BIDI_RE, (m, a, b) => a.replace(/(\s*)$/, '‎$1') + b);
        if (u !== v) n.nodeValue = u;
      }
    });
    if (/[؀-ۿ]/.test(document.title) && document.title.indexOf('‎') < 0)
      document.title = document.title.replace(BIDI_RE, (m, a, b) => a.replace(/(\s*)$/, '‎$1') + b);
  };

  const tick = () => { fixFontFallback(); fixBidi(); wire(); fixDiamondCopy(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tick, { once: true });
  tick();
  [300, 1000, 2000, 4000].forEach((d) => setTimeout(tick, d));
  setInterval(() => { fixDiamondCopy(); fixFontFallback(); fixBidi(); }, 1500);
})();
